import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../hd-styles.css';

const API_URL = '/api';

function AppLayout({ children, activeTab }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [networkStats, setNetworkStats] = useState(null);
  const accountHash = localStorage.getItem('avalw_account_hash');

  useEffect(() => {
    if (accountHash) {
      loadAccount();
      loadNetworkStats();
    }
  }, [accountHash]);

  const loadAccount = async () => {
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: accountHash })
      });
      const data = await res.json();
      if (data.success) {
        setAccount(data.data);
      }
    } catch (err) {
      console.error('Load error:', err);
    }
  };

  const loadNetworkStats = async () => {
    try {
      const res = await fetch(`${API_URL}/stats`);
      const data = await res.json();
      // Map API response to expected format
      setNetworkStats({
        online_workers: data.network?.workers_online || data.active_workers || 0,
        total_cores: data.network?.total_cores || 0,
        total_cache_hits: data.lifetime?.cache_served || data.lifetime?.queries_served || 0,
        co2_saved_kg: data.co2_impact?.saved_total_kg || 0
      });
    } catch (err) {
      console.error('Stats error:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('avalw_account_hash');
    localStorage.removeItem('display_name');
    navigate('/');
  };

  const isOnline = account?.worker_status === 'online';
  const badge = account?.badge || { name: 'Starter', color: '#64748b' };
  const currentPath = location.pathname;

  const isActive = (path) => {
    if (path === '/home-dashboard' && currentPath === '/home-dashboard') return true;
    if (path === '/network' && currentPath === '/network') return true;
    if (path === '/leaderboard' && currentPath === '/leaderboard') return true;
    if (path === '/rewards' && currentPath === '/rewards') return true;
    if (path === '/alliances' && (currentPath === '/alliances' || currentPath.startsWith('/alliance'))) return true;
    if (path === '/settings' && currentPath === '/settings') return true;
    return false;
  };

  return (
    <div className="app-container light">
      {/* Sidebar */}
      <aside className="app-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="logo-icon">
              <svg viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="14" fill="#10b981" fillOpacity="0.1"/>
                <path d="M16 6L24 11V21L16 26L8 21V11L16 6Z" fill="#10b981"/>
                <path d="M16 10L20 12.5V17.5L16 20L12 17.5V12.5L16 10Z" fill="white"/>
              </svg>
            </div>
            <span>AVALW</span>
          </div>
        </div>

        {/* Network Stats Mini */}
        <div className="sidebar-network-stats">
          <div className="sn-stat">
            <span className="sn-value">{networkStats?.online_workers || 0}</span>
            <span className="sn-label">Online</span>
          </div>
          <div className="sn-stat">
            <span className="sn-value">{networkStats?.total_cores || 0}</span>
            <span className="sn-label">Cores</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <span className="nav-section-title">Main</span>
            <Link to="/home-dashboard" className={`nav-item ${isActive('/home-dashboard') ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/></svg>
              <span>Dashboard</span>
            </Link>
            <Link to="/network" className={`nav-item ${isActive('/network') ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
              <span>Network Live</span>
              <span className="nav-badge live">LIVE</span>
            </Link>
          </div>

          <div className="nav-section">
            <span className="nav-section-title">Progress</span>
            <Link to="/leaderboard" className={`nav-item ${isActive('/leaderboard') ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7.5 21H2V9h5.5v12zm7.25-18h-5.5v18h5.5V3zM22 11h-5.5v10H22V11z"/></svg>
              <span>Leaderboard</span>
            </Link>
            <Link to="/rewards" className={`nav-item ${isActive('/rewards') ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2z"/></svg>
              <span>Rewards</span>
            </Link>
          </div>

          <div className="nav-section">
            <span className="nav-section-title">Community</span>
            <Link to="/alliances" className={`nav-item ${isActive('/alliances') ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>
              <span>Alliances</span>
            </Link>
          </div>

          <div className="nav-section">
            <span className="nav-section-title">Account</span>
            <Link to="/settings" className={`nav-item ${isActive('/settings') ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>
              <span>Settings</span>
            </Link>
            <button onClick={handleLogout} className="nav-item logout-btn">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
              <span>Logout</span>
            </button>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-card">
            <div className="user-avatar" style={{ backgroundColor: badge.color }}>
              {(account?.profile?.display_name || 'U')[0]}
            </div>
            <div className="user-details">
              <div className="user-name">{account?.profile?.display_name || 'User'}</div>
              <div className="user-status">
                <span className={`status-dot ${isOnline ? 'online' : 'offline'}`}></span>
                {isOnline ? 'Online' : 'Offline'}
              </div>
            </div>
            <div className="user-badge" style={{ backgroundColor: badge.color + '20', color: badge.color }}>
              {badge.name}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="app-main">
        {children}
      </main>
    </div>
  );
}

export default AppLayout;
